/**
 * Session Expiration Handler (Opaque Session)
 *
 * Patches window.fetch so that a 401 from a non-auth endpoint triggers a
 * clean client-side logout + redirect to /login. The backend owns token
 * rotation and session lifecycle; this component only reacts when the
 * backend has decided the session is truly dead.
 *
 * Mounts under QueryClientProvider + BrowserRouter + ToastProvider +
 * AuthProvider (see main.tsx for the wrapping order).
 */

import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext.js';
import { useToast } from '../../contexts/ToastContext.js';

export function SessionExpirationHandler() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { logout, isAuthenticated } = useAuth();
  const { showError } = useToast();

  const hasShownExpiration = useRef(false);
  const isProcessingLogout = useRef(false);

  const handleLogout = useCallback(async () => {
    if (hasShownExpiration.current || isProcessingLogout.current) return;

    isProcessingLogout.current = true;
    hasShownExpiration.current = true;

    // Cancel all in-flight queries to stop retries firing more 401s while we
    // tear down, then drop the cache so the next login doesn't show stale data.
    queryClient.cancelQueries();
    queryClient.clear();

    showError('Session Expired', 'Your session has expired. Please log in again.');

    try {
      await logout();
    } catch (error) {
      console.error('[SessionHandler] Logout failed:', error);
    }

    navigate('/login', { replace: true });

    // Refs stay latched until the user logs in again. Resetting them in the
    // same microtask as navigate() races with the route commit — any in-flight
    // fetch that resolves with 401 in that window would retrigger handleLogout,
    // stack another toast, and refire logout. The useEffect below clears
    // these flags once isAuthenticated becomes true (successful login).
  }, [logout, navigate, showError, queryClient]);

  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      if (
        response.status === 401 &&
        isAuthenticated &&
        !hasShownExpiration.current &&
        !isProcessingLogout.current
      ) {
        const url =
          typeof args[0] === 'string'
            ? args[0]
            : (args[0] as Request)?.url || '';

        // Skip auth + health endpoints — a 401 on /auth/login is a user error,
        // not session expiration; invoking handleLogout there would loop.
        if (
          !url.includes('/auth/') &&
          !url.includes('/logout') &&
          !url.includes('/health')
        ) {
          await handleLogout();
        }
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [isAuthenticated, handleLogout]);

  // Reset flags when user logs in (see comment in handleLogout).
  useEffect(() => {
    if (isAuthenticated) {
      hasShownExpiration.current = false;
      isProcessingLogout.current = false;
    }
  }, [isAuthenticated]);

  return null;
}
